import { IconActionCopy24, IconActionDelete24, IconProductInstantBooking16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackMessageMenuClick } from './tracking';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { useReplyTemplateModal } from '../../hooks';
import {
  currentChatOpponentIdSelector,
  currentOpenChatSelector,
  isMasterSelector,
  userSelector,
} from '../../selectors';
import { openDeleteMessageModal } from '../../slices/deleteMessageSlice';
import { copyMessage } from '../../thunks/copyMessage';
import { TChatMessageMenuItem } from '../../types/chatMessageMenuItem';
import { useApplicationContext } from '../../utils/applicationContext';
import { EReplyTemplateTrackingSource, trackReplyTemplateButtonClick } from '../ReplyTemplateModalContainer/tracking';

export const useMenuItems = (chatId?: string, messageId?: string): TChatMessageMenuItem[] => {
  const {
    config,
    custom: { messagesMap },
  } = useApplicationContext();
  const dispatch = useDispatch();

  const user = useSelector(userSelector);
  const isMaster = useSelector(isMasterSelector);
  const openedChat = useSelector(currentOpenChatSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);
  const { isReplyTemplateEnabled, openReplyTemplateModalCreate } = useReplyTemplateModal();

  const allowedTimeToDeleteMessage = config.get<number>('frontend-chats.allowedTimeToDeleteMessage') || 0;

  const chatMessage = messageId ? messagesMap.get(messageId) : undefined;
  const message = chatMessage?.message?.message;

  const canDelete = React.useMemo((): boolean => {
    if (!message || !openedChat) {
      return false;
    }

    // Нельзя удалять в чате с чат-ботом
    if (chatId === CHATBOT_ID) {
      return false;
    }

    // Нельзя удалить уже удалённое
    if (message.isDeleted) {
      return false;
    }

    const expired = Date.now() - message.created > allowedTimeToDeleteMessage;

    // Время для возможности удаления истекло
    if (expired) {
      return false;
    }

    // Сообщение отослано мной
    if (message.userId === user.userId) {
      return true;
    }

    const { users } = openedChat;
    // Я - мастер и сообщение субагента в трёхстороннем чате (мастер + субагент + клиент)
    const isThreeWayChat = users.some(({ chatRole }) => chatRole === 'master');
    const userChatRole = users.find(({ userId }) => userId === message.userId)?.chatRole;

    if (isMaster && isThreeWayChat && userChatRole === 'opponent') {
      return true;
    }

    return false;
  }, [message, openedChat, allowedTimeToDeleteMessage, user.userId, isMaster, chatId]);

  const canReplyTemplate = React.useMemo((): boolean => {
    if (!message || !isReplyTemplateEnabled || message.isDeleted) {
      return false;
    }

    // Только текстовые сообщения
    if (message.type !== 'text') {
      return false;
    }

    // Только свои сообщения
    return message.userId === user.userId;
  }, [isReplyTemplateEnabled, message, user.userId]);

  return React.useMemo(() => {
    if (!chatId || !messageId) {
      return [];
    }

    const items: TChatMessageMenuItem[] = [];

    if (!message?.isDeleted) {
      items.push({
        title: 'Скопировать',
        color: 'var(--text-primary-default)',
        handler(): void {
          trackMessageMenuClick({ chatId, label: 'Copy', opponentId });
          dispatch(copyMessage({ messageId }));
        },
        icon: <IconActionCopy24 />,
      });
    }

    if (canDelete) {
      items.push({
        title: 'Удалить',
        color: 'var(--theme-red)',
        handler(): void {
          if (!message) {
            return;
          }

          const withPhoto = Boolean(message.files && (message.files.images.length || message.files.documents?.length));

          trackMessageMenuClick({ chatId, label: 'Delete_message', opponentId, withPhoto });
          dispatch(openDeleteMessageModal({ chatId, messageId, placeType: 'chat' }));
        },
        icon: <IconActionDelete24 color="current_color" />,
      });
    }

    if (canReplyTemplate) {
      items.push({
        title: 'Сохранить как шаблон',
        color: 'theme-dark',
        icon: <IconProductInstantBooking16 />,
        handler(): void {
          if (!message) {
            return;
          }

          trackReplyTemplateButtonClick({ chatId, opponentId, source: EReplyTemplateTrackingSource.ChatMessage });
          openReplyTemplateModalCreate(EReplyTemplateTrackingSource.ChatMessage, message.message);
        },
      });
    }

    return items;
  }, [chatId, messageId, canDelete, canReplyTemplate, opponentId, dispatch, message, openReplyTemplateModalCreate]);
};
